import {
  BlockStack,
  InlineGrid,
  Text,
  SkeletonBodyText,
  SkeletonDisplayText,
  DataTable,
  Divider,
  InlineStack,
  Button,
  DescriptionList,
  Badge,
} from '@shopify/polaris';
import { useGetVehiclelog, useMutateCreatePassengerListing, useMutateDeleteVehiclelog, useMutateTripLogListing } from 'queries/vehicle_log.query';
import { useNavigate, useParams } from 'react-router-dom';

import __helpers from 'helpers/index';
import { DeleteIcon, EditIcon, ExternalIcon, LinkIcon, PageDownIcon } from '@shopify/polaris-icons';
import { useNotification } from 'context/NotificationContext';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import ListLogEditModal from './list_log.edit.modal';
import { ENUM_INVOICE_STATUS, TypedInvoices } from 'queries/invoices.query';
import dayjs from '../../utils/dayjs';

export default function ListLogDetail() {
  const REACT_APP_AJAX_UPLOAD_PERMALINK = process.env.REACT_APP_AJAX_UPLOAD_PERMALINK;
  const [showModal, setShowModal] = useState(false);
  const [currentLogId, setCurrentLogId] = useState<string>(null);

  let { vehicle_log_id, vehicle_id } = useParams();
  const { data: vehicleLogData, isLoading: loadingVehicleLogData } = useGetVehiclelog(vehicle_log_id);
  /** Xóa thì hỏi  */
  const { showDeleteConfirm } = useDeleteConfirm();
  const { mutateAsync: deleteVehicleLog } = useMutateDeleteVehiclelog();
  const deleteVehicleLogCallback = useCallback(
    async (vehicle_log_id: any) => {
      let confirmCancelTicket = await showDeleteConfirm('Bạn có chắc muốn xóa?', `Đây là việc không thể hoàn tác, vui lòng thao tác cẩn thận.`);
      try {
        if (confirmCancelTicket) await deleteVehicleLog(vehicle_log_id);
      } catch (e) {}
    },
    [deleteVehicleLog, showDeleteConfirm]
  );

  /** Tạo  */
  /* End form */
  const { addNotification } = useNotification();
  const currentVehicleLogCreatingPDFPassengerListing = useRef(null);
  const currentVehicleLogCreatingVehicleLogPDF = useRef(null);
  const { mutateAsync: createPassengerListing, isPending: creatingPassengerListing } = useMutateCreatePassengerListing();
  const createPassengerListingCallback = useCallback(async (trip_manifest_id) => {
    try {
      await createPassengerListing(trip_manifest_id);
      addNotification('info', 'Tạo bảng kê khách thành công! Vui lòng chờ vài giây để hệ thống tạo file và cập nhật link tải về.');
    } catch (e) {
      addNotification('error', 'Không thể tạo bảng kê khách!');
    }
  }, []);

  const { mutateAsync: createTriplog, isPending: creatingTriplog } = useMutateTripLogListing();
  const createTripLogCallback = useCallback(async (trip_manifest_id) => {
    try {
      await createTriplog(trip_manifest_id);
      addNotification('info', 'Tạo nhật trình thành công, đợi một tới hai giây để hệ thống tạo file PDF.');
    } catch (e) {
      addNotification('error', 'Không thể tạo nhật trình!');
    }
  }, []);

  /** lấy độc invoice ra */
  const [invoiceData, setInvoiceData] = useState<TypedInvoices[]>([]);
  useEffect(() => {
    //
    const invoices = vehicleLogData?.trip_manifest?.orders?.flatMap((order) => (order.invoices ? [order.invoices] : [])) ?? [];
    setInvoiceData(invoices);
  }, [vehicleLogData]);

  const navigate = useNavigate();

  return (
    <div className="ABCDEFGH">
      {loadingVehicleLogData ? (
        <BlockStack gap="400">
          <SkeletonDisplayText />
          <SkeletonBodyText />
          <SkeletonBodyText />
          <SkeletonBodyText />
        </BlockStack>
      ) : null}

      <InlineGrid gap={'200'} columns={['oneThird', 'twoThirds']}>
        <BlockStack gap="200">
          <Text as="h3" variant="headingLg">
            {vehicleLogData?.title}
          </Text>
          <br />

          <DescriptionList
            gap="tight"
            items={[
              {
                term: 'Loại di chuyển',
                description: vehicleLogData?.trip_type,
              },
              {
                term: 'Biển số xe',
                description: vehicleLogData?.vehicle_data?.license_plate,
              },
              {
                term: 'Tài xế',
                description: vehicleLogData?.driver_data?.display_name || 'Chưa có tài xế',
              },
              {
                term: 'Doanh thu',
                description: __helpers.formatNumberWithCommas(vehicleLogData?.trip_manifest?.total_fare || 0),
              },
              {
                term: 'Khoảng cách',
                description: vehicleLogData?.distance + ' KM',
              },
              {
                term: 'Ngày di chuyển',
                description: dayjs(new Date(Number(vehicleLogData?.createdAt))).format('DD/MM/YYYY'),
              },
              {
                term: 'Đi từ',
                description: vehicleLogData?.start_address + ' lúc ' + vehicleLogData?.start_time,
              },
              {
                term: 'Đến',
                description: vehicleLogData?.end_address + ' lúc ' + vehicleLogData?.end_time,
              },
            ]}
          />

          <Divider />
          <InlineStack gap="200">
            {vehicleLogData?.file_passenger_listing ? (
              <Button
                variant="plain"
                icon={PageDownIcon}
                tone="success"
                url={`${REACT_APP_AJAX_UPLOAD_PERMALINK}/${vehicleLogData?.file_passenger_listing}`}
                target="_blank"
              >
                Tải xuống bảng kê
              </Button>
            ) : (
              <Button
                variant="plain"
                icon={PageDownIcon}
                loading={creatingPassengerListing && currentVehicleLogCreatingPDFPassengerListing.current === vehicleLogData?.id}
                onClick={() => {
                  currentVehicleLogCreatingPDFPassengerListing.current = vehicleLogData?.id;
                  createPassengerListingCallback(vehicleLogData?.id);
                }}
              >
                Tạo bảng kê khách
              </Button>
            )}
            {vehicleLogData?.file_trip_log ? (
              <Button
                variant="plain"
                icon={PageDownIcon}
                size="medium"
                tone="success"
                url={`${REACT_APP_AJAX_UPLOAD_PERMALINK}/${vehicleLogData?.file_trip_log}`}
                target="_blank"
              >
                Tải nhật trình
              </Button>
            ) : (
              <Button
                variant="plain"
                icon={PageDownIcon}
                loading={creatingTriplog && currentVehicleLogCreatingVehicleLogPDF.current === vehicleLogData?.id}
                onClick={() => {
                  currentVehicleLogCreatingVehicleLogPDF.current = vehicleLogData?.id;
                  createTripLogCallback(vehicleLogData?.id);
                }}
              >
                Tạo nhật trình
              </Button>
            )}

            <Button
              variant="plain"
              icon={DeleteIcon}
              onClick={() => {
                deleteVehicleLogCallback(vehicleLogData?.id);
              }}
            >
              Xóa
            </Button>

            <Button
              variant="plain"
              icon={EditIcon}
              onClick={() => {
                setCurrentLogId(vehicleLogData?.id);
                setShowModal(true);
              }}
            >
              Chỉnh
            </Button>

            <Button
              variant="plain"
              icon={ExternalIcon}
              onClick={() => {
                navigate(
                  `/ticket_managers?d=${dayjs(Number(vehicleLogData?.trip_manifest?.departure_date)).utcOffset(7).format('YYYY-MM-DD')}&journey_id=${vehicleLogData?.trip_manifest?.trip?.journey_id}&h=${vehicleLogData?.trip_manifest?.time_id}`
                );
              }}
            >
              Đi tới chuyến
            </Button>
          </InlineStack>
          <br />
        </BlockStack>
        <div className="">
          <Text as="h3" variant="headingLg">
            Danh sách vé
          </Text>
          <br />

          {!__helpers.isEmpty(vehicleLogData?.trip_manifest?.orders) ? (
            <DataTable
              hideScrollIndicator
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'numeric']}
              headings={['STT', 'Mã đơn hàng', 'Hành khách', 'Số vé', 'Đơn vị', 'Đón tại', 'Trả tại', 'Giá vé']}
              rows={vehicleLogData?.trip_manifest?.orders?.map((orderElement, index) => {
                return [
                  index,
                  orderElement?.order_code,
                  orderElement?.customer_data?.display_name,
                  orderElement?.total_seat,
                  orderElement?.ticket[0]?.ticket_type,
                  orderElement?.pickup_address,
                  orderElement?.dropoff_address,
                  __helpers.formatNumberWithCommas(orderElement?.total_must_pay || 0),
                ];
              })}
            />
          ) : (
            <Text as="p" variant="headingMd" tone="subdued">
              Không có khách nào trong chuyến này!
            </Text>
          )}

          <br />
          <Divider />
          <br />

          <Text as="h3" variant="headingLg">
            Danh sách hóa đơn
          </Text>
          <br />
          {!__helpers.isEmpty(invoiceData) ? (
            <DataTable
              hideScrollIndicator
              columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric']}
              headings={['STT', 'Số hóa đơn', 'Viết cho', 'Giá trị', 'Thuế', 'Tổng', 'Tình trạng']}
              rows={invoiceData.map((invoiceData, index) => {
                return [
                  index,
                  invoiceData?.shDon,
                  invoiceData?.NMua_Ten || invoiceData?.NMua_HVTNMHang,
                  __helpers.formatNumberWithCommas(invoiceData?.TgThTien || 0),
                  __helpers.formatNumberWithCommas(invoiceData?.TgTThue || 0),
                  __helpers.formatNumberWithCommas(invoiceData?.TgTTTBSo || 0),
                  invoiceData?.invoice_status === ENUM_INVOICE_STATUS.CANCEL ? (
                    'ĐÃ HỦY'
                  ) : invoiceData?.signedAt ? (
                    <Badge tone="success">Đã ký</Badge>
                  ) : (
                    <Badge tone="critical">Chưa ký</Badge>
                  ),
                ];
              })}
            />
          ) : (
            <Text as="p" variant="headingMd" tone="subdued">
              Chưa có hóa đơn nào trong chuyến này!
            </Text>
          )}
        </div>
      </InlineGrid>

      <ListLogEditModal
        show={showModal}
        vehicle_id={vehicle_id}
        currentLogId={currentLogId}
        onClose={() => {
          setShowModal(false);
          setCurrentLogId(null);
        }}
      />
    </div>
  );
}

